/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { AcaConfigFolderService } from '../shared/aca-config-folder.service';

/** Where the deployed model file sits. */
const MODEL_PATH = 'Data Dictionary/Models/dmsModel.xml';

/** Where the labels for the types live, beside the rest of the app's own data. */
const LABELS_FOLDER = 'Data Dictionary/ACA';
const LABELS_FILE = 'document-types.json';

/**
 * Adding, renaming and removing what the content model defines: the document
 * types, and the metadata fields every managed document carries.
 *
 * The allowed values are a LIST constraint inside the repository's own content
 * model, so a change means editing that model. The deploy script deactivates the
 * model before replacing it, but that route is closed here: once documents carry
 * the aspect the repository refuses to deactivate it at all ("model is in use").
 * Writing the new XML over the active model works instead — the repository
 * validates it on the way in and reloads the dictionary itself, rejecting any
 * change the existing content could not satisfy. A rejected write leaves the
 * model untouched; the previous XML is put back regardless, so a half-written
 * model cannot survive an error.
 *
 * Labels are kept out of the model on purpose. A LIST constraint holds bare
 * codes; Alfresco reads its display text from a resource bundle, which a model
 * uploaded at runtime cannot carry. They live in a small JSON file instead,
 * which is also what lets a type be renamed without touching the model at all.
 */
@Injectable({ providedIn: 'root' })
export class ContentModelAdminService {
  private readonly api = inject(AlfrescoApiService);
  private readonly configFolder = inject(AcaConfigFolderService);
  private readonly dms = inject(DmsMetadataService);
  private canEdit$?: Observable<boolean>;

  private get nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  // ── the model file ─────────────────────────────────────────────────────────

  private modelNode(): Observable<{ id: string }> {
    return from(this.nodes.getNode('-root-', { relativePath: MODEL_PATH } as any) as Promise<any>).pipe(map((res) => ({ id: res.entry.id })));
  }

  private modelXml(id: string): Observable<string> {
    return from(this.nodes.getNodeContent(id) as Promise<any>).pipe(
      switchMap((data: any) => from(typeof data?.text === 'function' ? (data.text() as Promise<string>) : Promise.resolve(String(data ?? ''))))
    );
  }

  private putXml(id: string, xml: string): Observable<any> {
    return from(this.nodes.updateNodeContent(id, xml, { majorVersion: true } as any) as Promise<any>);
  }

  /**
   * Writes `xml` over the model, putting `previous` back if the repository will
   * not take it.
   */
  private replaceModel(id: string, previous: string, xml: string): Observable<any> {
    return this.putXml(id, xml).pipe(
      catchError((error) =>
        // put the model back exactly as it was, then report the original failure
        this.putXml(id, previous).pipe(
          catchError(() => of(null)),
          switchMap(() => throwError(() => error))
        )
      ),
      tap(() => this.dms.reset())
    );
  }

  /** The codes a field currently allows, in the order the model lists them. */
  private readValues(xml: string, fieldId: string): string[] {
    const list = this.valueList(xml, fieldId);
    return list ? [...list.matchAll(/<value>\s*([^<]*?)\s*<\/value>/g)].map((match) => match[1]) : [];
  }

  /**
   * The `<constraint>` that holds a field's allowed values.
   *
   * A model may write it either way: inside the property itself, or once at the
   * top under a name the property then refers to. Both are read here so the
   * screen never has to care which the model happened to use.
   */
  private constraintFor(xml: string, fieldId: string): string | null {
    const property = this.propertyBlock(xml, fieldId);
    if (!property) {
      return null;
    }
    const inline = /<constraint\b[^>]*type="LIST"[\s\S]*?<\/constraint>/.exec(property);
    if (inline) {
      return inline[0];
    }
    const ref = /<constraint\s+ref="([^"]+)"/.exec(property)?.[1];
    if (!ref) {
      return null;
    }
    const named = new RegExp(`<constraint\\b[^>]*name="${ref}"[\\s\\S]*?</constraint>`).exec(xml);
    return named ? named[0] : null;
  }

  /** The `<list>…</list>` of a field's constraint, if it has one. */
  private valueList(xml: string, fieldId: string): string | null {
    const constraint = this.constraintFor(xml, fieldId);
    if (!constraint) {
      return null;
    }
    const list = /<list>[\s\S]*?<\/list>/.exec(constraint);
    return list ? list[0] : null;
  }

  /** The model with one field's value list replaced, indentation kept. */
  private writeValues(xml: string, fieldId: string, values: string[]): string {
    const constraint = this.constraintFor(xml, fieldId);
    const list = constraint ? /<list>[\s\S]*?<\/list>/.exec(constraint)?.[0] : null;
    if (!constraint || !list) {
      throw new Error('no value list on this field');
    }
    const indent = /\n(\s*)<value>/.exec(list)?.[1] ?? '          ';
    const closing = /\n(\s*)<\/list>/.exec(list)?.[1] ?? '        ';
    const rebuilt = `<list>\n${values.map((value) => `${indent}<value>${this.escape(value)}</value>`).join('\n')}\n${closing}</list>`;
    // the constraint text is swapped whole, so an identical list on another
    // field can never be the one that gets rewritten
    return xml.replace(constraint, constraint.replace(list, rebuilt));
  }

  // ── the labels ─────────────────────────────────────────────────────────────

  /** Reads the label file, treating a missing one as "no custom labels yet". */
  labels(): Observable<Record<string, string>> {
    return this.configFolder.find(LABELS_FILE).pipe(
      switchMap((id) => this.modelXmlAsJson(id)),
      catchError(() => of({} as Record<string, string>))
    );
  }

  private modelXmlAsJson(id: string): Observable<Record<string, string>> {
    return this.modelXml(id).pipe(
      map((text) => {
        try {
          const parsed = JSON.parse(text);
          return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
        } catch {
          return {};
        }
      })
    );
  }

  private saveLabels(labels: Record<string, string>): Observable<any> {
    const body = JSON.stringify(labels, null, 2);
    return this.configFolder.find(LABELS_FILE).pipe(
      catchError(() => this.createLabelFile().pipe(tap(() => this.configFolder.forget()))),
      switchMap((id) => from(this.nodes.updateNodeContent(id, body) as Promise<any>)),
      tap(() => {
        DmsMetadataService.setCustomLabels(labels);
        // the schema caches each option with the label it had when it was read
        this.dms.reset();
      })
    );
  }

  private createLabelFile(): Observable<string> {
    return from(this.nodes.getNode('-root-', { relativePath: LABELS_FOLDER } as any) as Promise<any>).pipe(
      catchError(() =>
        from(this.nodes.getNode('-root-', { relativePath: 'Data Dictionary' } as any) as Promise<any>).pipe(
          switchMap((dictionary: any) =>
            from(this.nodes.createNode(dictionary.entry.id, { name: 'ACA', nodeType: 'cm:folder' } as any) as Promise<any>)
          )
        )
      ),
      switchMap((folder: any) => from(this.nodes.createNode(folder.entry.id, { name: LABELS_FILE, nodeType: 'cm:content' } as any) as Promise<any>)),
      map((res: any) => res.entry.id as string)
    );
  }

  // ── the values a list field allows ─────────────────────────────────────────

  /** The codes a field allows right now, straight from the model. */
  values(fieldId: string): Observable<string[]> {
    return this.modelNode().pipe(
      switchMap(({ id }) => this.modelXml(id)),
      map((xml) => this.readValues(xml, fieldId)),
      catchError(() => of([] as string[]))
    );
  }

  /** Adds one value: the code goes into the model, the name beside it. */
  addValue(fieldId: string, value: string, label: string): Observable<any> {
    const code = ContentModelAdminService.toCode(value);
    if (!code) {
      return throwError(() => new Error('empty code'));
    }
    return this.modelNode().pipe(
      switchMap(({ id }) =>
        this.modelXml(id).pipe(
          switchMap((xml) => {
            const current = this.readValues(xml, fieldId);
            if (current.includes(code)) {
              return throwError(() => new Error('duplicate'));
            }
            return this.replaceModel(id, xml, this.writeValues(xml, fieldId, [...current, code]));
          })
        )
      ),
      switchMap(() => this.renameValue(code, label))
    );
  }

  /**
   * Renames one value. Only the label moves — the code stays as it is, because
   * every document already carrying it refers to the code and nothing else.
   */
  renameValue(value: string, label: string): Observable<any> {
    return this.labels().pipe(
      switchMap((labels) => {
        const next = { ...labels };
        if (label.trim()) {
          next[value] = label.trim();
        } else {
          delete next[value];
        }
        return this.saveLabels(next);
      })
    );
  }

  /**
   * Takes one value out of a field's list. The caller checks first that no
   * document carries it: unlike a deleted property, the repository accepts a
   * narrowed list without complaint, so nothing else would notice.
   */
  removeValue(fieldId: string, value: string): Observable<any> {
    return this.modelNode().pipe(
      switchMap(({ id }) =>
        this.modelXml(id).pipe(
          switchMap((xml) => {
            const remaining = this.readValues(xml, fieldId).filter((entry) => entry !== value);
            return this.replaceModel(id, xml, this.writeValues(xml, fieldId, remaining));
          })
        )
      ),
      switchMap(() => this.renameValue(value, ''))
    );
  }

  // ── the metadata fields ────────────────────────────────────────────────────

  /**
   * Adds a property to the aspect. Widening the model like this is a change the
   * repository accepts on a model that is already in use.
   */
  addField(code: string, label: string, dataType: string, defaultValue: string, options: string[]): Observable<any> {
    const id = `dms:${ContentModelAdminService.toName(code)}`;
    if (id === 'dms:') {
      return throwError(() => new Error('empty code'));
    }
    return this.modelNode().pipe(
      switchMap(({ id: node }) =>
        this.modelXml(node).pipe(
          switchMap((xml) => {
            if (xml.includes(`<property name="${id}"`)) {
              return throwError(() => new Error('duplicate'));
            }
            const block = this.propertyXml(id, label, dataType, defaultValue, options);
            const next = xml.replace('      </properties>', `${block}      </properties>`);
            return this.replaceModel(node, xml, next);
          })
        )
      ),
      switchMap(() => this.renameValue(id, label))
    );
  }

  /**
   * Changes what a field may hold: its default and, for a list, the values it
   * offers. The name and the data type are left alone — the repository refuses
   * to retype a property that documents already carry, and a renamed property
   * would leave their values behind.
   */
  updateField(id: string, label: string, defaultValue: string, options: string[]): Observable<any> {
    return this.modelNode().pipe(
      switchMap(({ id: node }) =>
        this.modelXml(node).pipe(
          switchMap((xml) => {
            const current = this.propertyBlock(xml, id);
            if (!current) {
              return throwError(() => new Error('not found'));
            }
            const type = /<type>\s*([^<]+?)\s*<\/type>/.exec(current)?.[1] ?? 'd:text';
            const title = /<title>\s*([^<]*?)\s*<\/title>/.exec(current)?.[1] ?? id;
            const rebuilt = this.propertyXml(id, title, type, defaultValue, options);
            return this.replaceModel(node, xml, xml.replace(current, rebuilt));
          })
        )
      ),
      switchMap(() => this.renameValue(id, label))
    );
  }

  /**
   * Removes a property. The repository validates this itself and refuses while
   * any document still carries a value, which is the answer the screen reports.
   */
  removeField(id: string): Observable<any> {
    return this.modelNode().pipe(
      switchMap(({ id: node }) =>
        this.modelXml(node).pipe(
          switchMap((xml) => {
            const block = this.propertyBlock(xml, id);
            if (!block) {
              return throwError(() => new Error('not found'));
            }
            return this.replaceModel(node, xml, xml.replace(block, ''));
          })
        )
      ),
      switchMap(() => this.renameValue(id, ''))
    );
  }

  /** One `<property>` element, with an inline list constraint when it needs one. */
  private propertyXml(id: string, title: string, dataType: string, defaultValue: string, options: string[]): string {
    const tokenised = dataType === 'd:text' && !options.length ? 'both' : 'false';
    const lines = [`        <property name="${id}">`, `          <title>${this.escape(title)}</title>`, `          <type>${dataType}</type>`];
    if (defaultValue.trim()) {
      lines.push(`          <default>${this.escape(defaultValue.trim())}</default>`);
    }
    lines.push('          <index enabled="true">', `            <tokenised>${tokenised}</tokenised>`, '          </index>');
    if (options.length) {
      lines.push(
        '          <constraints>',
        '            <constraint type="LIST">',
        '              <parameter name="allowedValues">',
        '                <list>',
        ...options.map((value) => `                  <value>${this.escape(value)}</value>`),
        '                </list>',
        '              </parameter>',
        '            </constraint>',
        '          </constraints>'
      );
    }
    lines.push('        </property>');
    return lines.join('\n') + '\n';
  }

  /** The `<property>` element for `id`, exactly as the model spells it. */
  private propertyBlock(xml: string, id: string): string | null {
    const found = new RegExp(`[ \\t]*<property name="${id}">[\\s\\S]*?</property>\\n`).exec(xml);
    return found ? found[0] : null;
  }

  private escape(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /** A local name Alfresco accepts for a property. */
  static toName(value: string): string {
    const cleaned = value
      .trim()
      .replace(/^dms:/, '')
      .replace(/[^A-Za-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map((word, index) => (index ? word.charAt(0).toUpperCase() + word.slice(1) : word.charAt(0).toLowerCase() + word.slice(1)))
      .join('');
    return cleaned.replace(/^[^A-Za-z]+/, '').slice(0, 40);
  }

  /**
   * Whether this repository lets the signed-in person edit the model at all.
   * Two round trips, so the answer is kept for the rest of the session.
   */
  canEdit(): Observable<boolean> {
    if (!this.canEdit$) {
      this.canEdit$ = this.modelNode().pipe(
        switchMap(({ id }) => from(this.nodes.getNode(id, { include: ['allowableOperations'] } as any) as Promise<any>)),
        map((res: any) => (res?.entry?.allowableOperations ?? []).includes('update')),
        catchError(() => of(false)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.canEdit$;
  }

  /** A code Alfresco accepts inside a LIST constraint. */
  static toCode(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
  }
}
